import { useEffect, useState } from "react";
import { Loader2, LogIn, LogOut, Clock, AlertTriangle, UserX } from "lucide-react";
import parentApi from "@/lib/parentApi";
import { ExcuseRequests } from "@/components/parent/ExcuseRequests";

const KIND = {
  drop_off: { label: "Checked in", icon: LogIn, cls: "bg-emerald-100 text-emerald-700" },
  pickup: { label: "Picked up", icon: LogOut, cls: "bg-slate-100 text-slate-700" },
  tardy: { label: "Arrived late", icon: Clock, cls: "bg-amber-100 text-amber-700" },
  early_dismissal: { label: "Early dismissal", icon: AlertTriangle, cls: "bg-orange-100 text-orange-700" },
  absent: { label: "Marked absent", icon: UserX, cls: "bg-rose-100 text-rose-700" },
};

export function StudentAttendance({ studentId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    parentApi.get(`/parent/students/${studentId}/attendance`).then(({ data: d }) => setData(d)).catch(() => setData({ events: [], absences: [] }));
  }, [studentId]);

  if (data === null) return <div className="rounded-2xl bg-white p-10 text-center shadow-sm"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  const rows = [
    ...data.events.map((e) => ({ id: e.id, kind: e.event_type, at: e.occurred_at, who: e.picker_name || e.officer_name })),
    ...data.absences.map((a) => ({ id: a.id, kind: "absent", at: a.date + "T12:00:00", who: a.marked_by, dateOnly: true })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));

  const counts = { present: new Set(data.events.filter((e) => e.event_type === "drop_off").map((e) => e.occurred_at.slice(0, 10))).size, tardy: data.events.filter((e) => e.event_type === "tardy").length, absent: data.absences.length };

  return (
    <div className="space-y-3" data-testid="parent-student-attendance">
      <ExcuseRequests studentId={studentId} />
      <div className="grid grid-cols-3 gap-2">
        {[["Days present", counts.present, "text-emerald-700"], ["Tardies", counts.tardy, "text-amber-700"], ["Absences", counts.absent, "text-rose-700"]].map(([l, v, c]) => (
          <div key={l} className="rounded-2xl bg-white shadow-sm p-3 text-center" data-testid={`parent-att-${l.toLowerCase().replace(" ", "-")}`}>
            <div className={`font-display text-2xl font-bold ${c}`}>{v}</div>
            <div className="text-[11px] text-slate-500">{l}</div>
          </div>
        ))}
      </div>
      <section className="rounded-2xl bg-white shadow-sm p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Last 30 days</div>
        {rows.length === 0 && <div className="text-sm text-slate-400" data-testid="parent-att-empty">No attendance records yet.</div>}
        <ol className="divide-y divide-slate-100">
          {rows.map((r) => { const k = KIND[r.kind] || KIND.drop_off; const I = k.icon; return (
            <li key={r.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`parent-att-row-${r.id}`}>
              <span className={`h-7 w-7 rounded-full inline-flex items-center justify-center ${k.cls}`}><I className="h-3.5 w-3.5" /></span>
              <span className="flex-1 min-w-0"><span className="text-slate-900 font-medium">{k.label}</span>{r.who && <span className="text-xs text-slate-500"> · {r.who}</span>}</span>
              <span className="text-xs text-slate-500 whitespace-nowrap">{new Date(r.at).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}{!r.dateOnly && ` · ${new Date(r.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}</span>
            </li>
          ); })}
        </ol>
      </section>
    </div>
  );
}
